import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useCreateCommande, useSeances } from '../api/hooks';
import type { InfoPlacement, NewBilletInput, PolitiqueTransfert, TypeCodeBarre } from '../api/types';
import { POLITIQUE_TRANSFERT, TYPE_CODE_BARRE } from '../lib/labels';
import { formatDate, fromLocalInput } from '../lib/format';
import { Button, Checkbox, ErrorBox, Field, Input, PageHeader, Section, Select } from '../components/ui';
import { useToast } from '../components/toast';

interface BilletForm extends NewBilletInput {
  key: number;
}

let nextKey = 1;

function emptyBillet(): BilletForm {
  return {
    key: nextKey++,
    prixBillet: 27.2,
    withBarcode: true,
    infoPlacement: [
      { LibellePlace: 'Catégorie', ValeurPlace: 'UNIQUE' },
      { LibellePlace: 'Tarif', ValeurPlace: 'TARIF NORMAL' },
    ],
  };
}

export default function CommandeNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: seances } = useSeances();
  const createCommande = useCreateCommande();

  const [idSeance, setIdSeance] = useState('');
  const [nom, setNom] = useState('PAIN');
  const [prenom, setPrenom] = useState('Ruben');
  const [eticket, setEticket] = useState(true);
  const [transfert, setTransfert] = useState<PolitiqueTransfert>(1);
  const [differe, setDiffere] = useState(false);
  const [dateAffichage, setDateAffichage] = useState('');
  const [typeCodeBarre, setTypeCodeBarre] = useState<TypeCodeBarre>(1);
  const [texteComplement, setTexteComplement] = useState('');
  const [billets, setBillets] = useState<BilletForm[]>([emptyBillet()]);

  const seanceId = idSeance || (seances?.[0] ? String(seances[0].idSeance) : '');

  const patchBillet = (key: number, patch: Partial<BilletForm>) =>
    setBillets((current) => current.map((b) => (b.key === key ? { ...b, ...patch } : b)));

  const patchPlacement = (key: number, index: number, patch: Partial<InfoPlacement>) =>
    setBillets((current) =>
      current.map((b) =>
        b.key === key
          ? { ...b, infoPlacement: b.infoPlacement.map((p, i) => (i === index ? { ...p, ...patch } : p)) }
          : b,
      ),
    );

  const submit = () => {
    if (!seanceId) {
      toast.push('error', 'Sélectionnez une séance');
      return;
    }
    createCommande.mutate(
      {
        idSeance: Number(seanceId),
        nom,
        prenom,
        eticket,
        transfert,
        envoiCodeBarreDiffere: differe,
        dateAffichageCodeBarre: fromLocalInput(dateAffichage),
        texteComplement: texteComplement || null,
        typeCodeBarre,
        billets: billets.map(({ prixBillet, infoPlacement, withBarcode }) => ({
          prixBillet,
          infoPlacement,
          withBarcode,
        })),
      },
      {
        onSuccess: (result) => {
          toast.success(`Commande ${result.commande.idCommande} créée`);
          navigate(`/commandes/${result.commande.guid}`);
        },
        onError: (error) => toast.error(error),
      },
    );
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Nouvelle commande" />

      {createCommande.error ? <ErrorBox error={createCommande.error} /> : null}

      <Section title="Commande">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Séance">
            <Select value={seanceId} onChange={(e) => setIdSeance(e.target.value)}>
              {(seances ?? []).map((seance) => (
                <option key={seance.idSeance} value={seance.idSeance}>
                  #{seance.idSeance} — {formatDate(seance.dateSeance)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Nom">
            <Input value={nom} onChange={(e) => setNom(e.target.value)} />
          </Field>
          <Field label="Prénom">
            <Input value={prenom} onChange={(e) => setPrenom(e.target.value)} />
          </Field>
          <Field label="Politique de transfert">
            <Select
              value={transfert}
              onChange={(e) => setTransfert(Number(e.target.value) as PolitiqueTransfert)}
            >
              {Object.entries(POLITIQUE_TRANSFERT).map(([value, label]) => (
                <option key={value} value={value}>
                  {value} — {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Type de code barre" hint="0 affiche un Code128, toute autre valeur un QR code">
            <Select
              value={typeCodeBarre}
              onChange={(e) => setTypeCodeBarre(Number(e.target.value) as TypeCodeBarre)}
            >
              {Object.entries(TYPE_CODE_BARRE).map(([value, label]) => (
                <option key={value} value={value}>
                  {value} — {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date de dévoilage" hint="Laisser vide pour un dévoilage à J-4h">
            <Input
              type="datetime-local"
              value={dateAffichage}
              onChange={(e) => setDateAffichage(e.target.value)}
            />
          </Field>
          <Field label="Texte de complément">
            <Input value={texteComplement} onChange={(e) => setTexteComplement(e.target.value)} />
          </Field>
          <div className="flex flex-col justify-end gap-2 text-sm text-on-surface-variant">
            <label className="flex items-center gap-2">
              <Checkbox checked={eticket} onChange={(e) => setEticket(e.target.checked)} />
              eticket (requis pour le bouton PDF)
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={differe} onChange={(e) => setDiffere(e.target.checked)} />
              Envoi du code barre différé
            </label>
          </div>
        </div>
      </Section>

      <Section
        title={`Billets (${billets.length})`}
        actions={
          <Button size="sm" onClick={() => setBillets((current) => [...current, emptyBillet()])}>
            <Plus size={14} aria-hidden />
            Ajouter un billet
          </Button>
        }
      >
        <div className="space-y-3">
          {billets.map((billet, index) => (
            <div key={billet.key} className="rounded-lg border border-line bg-bg p-3">
              <div className="flex flex-wrap items-end gap-4">
                <Field label={`Billet ${index + 1} — prix`}>
                  <Input
                    type="number"
                    step="0.01"
                    value={billet.prixBillet}
                    onChange={(e) => patchBillet(billet.key, { prixBillet: Number(e.target.value) })}
                  />
                </Field>
                <label className="flex items-center gap-2 pb-2 text-sm text-on-surface-variant">
                  <Checkbox
                    checked={billet.withBarcode}
                    onChange={(e) => patchBillet(billet.key, { withBarcode: e.target.checked })}
                  />
                  avec code barre
                </label>
                <Button
                  size="sm"
                  variant="ghost"
                  className="mb-1 ml-auto"
                  disabled={billets.length === 1}
                  onClick={() => setBillets((current) => current.filter((b) => b.key !== billet.key))}
                  aria-label={`Supprimer le billet ${index + 1}`}
                >
                  <Trash2 size={15} aria-hidden />
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {billet.infoPlacement.map((placement, placementIndex) => (
                  <div key={placementIndex} className="flex flex-wrap gap-2">
                    <Input
                      aria-label={`Libellé de placement ${placementIndex + 1} du billet ${index + 1}`}
                      value={placement.LibellePlace}
                      onChange={(e) => patchPlacement(billet.key, placementIndex, { LibellePlace: e.target.value })}
                    />
                    <Input
                      aria-label={`Valeur de placement ${placementIndex + 1} du billet ${index + 1}`}
                      value={placement.ValeurPlace}
                      onChange={(e) => patchPlacement(billet.key, placementIndex, { ValeurPlace: e.target.value })}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        patchBillet(billet.key, {
                          infoPlacement: billet.infoPlacement.filter((_, i) => i !== placementIndex),
                        })
                      }
                      aria-label={`Supprimer la ligne de placement ${placementIndex + 1}`}
                    >
                      <Trash2 size={14} aria-hidden />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  onClick={() =>
                    patchBillet(billet.key, {
                      infoPlacement: [...billet.infoPlacement, { LibellePlace: '', ValeurPlace: '' }],
                    })
                  }
                >
                  <Plus size={14} aria-hidden />
                  Ligne de placement
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="flex gap-2">
        <Button variant="primary" onClick={submit} disabled={createCommande.isPending}>
          {createCommande.isPending ? 'Création…' : 'Créer la commande'}
        </Button>
        <Button onClick={() => navigate('/')}>Annuler</Button>
      </div>
    </div>
  );
}
